import React, { useEffect, useState, useRef } from 'react';
import { motion, animate } from 'framer-motion';
import { Users, Code2, Calculator as CalcIcon, AlertCircle, TrendingUp } from 'lucide-react';
const TOOLS = [{
  id: 'copilot',
  name: 'GitHub Copilot',
  basePrice: 39,
  type: 'per_seat',
  description: 'Enterprise tier'
}, {
  id: 'cursor',
  name: 'Cursor',
  basePrice: 40,
  type: 'per_seat',
  description: 'Business tier'
}, {
  id: 'claude',
  name: 'Claude Code',
  basePrice: 35,
  type: 'consumption',
  description: 'Avg. API usage'
}, {
  id: 'windsurf',
  name: 'Windsurf',
  basePrice: 40,
  type: 'per_seat',
  description: 'Team tier'
}, {
  id: 'augment',
  name: 'Augment Code',
  basePrice: 60,
  type: 'consumption',
  description: 'Message-based credits'
}, {
  id: 'gemini',
  name: 'Gemini Code Assist',
  basePrice: 45,
  type: 'per_seat',
  description: 'Enterprise tier'
}, {
  id: 'codex',
  name: 'OpenAI Codex',
  basePrice: 30,
  type: 'consumption',
  description: 'Avg. API usage'
}];
const AnimatedNumber = ({
  value,
  prefix = ''
}: {
  value: number;
  prefix?: string;
}) => {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const prevValue = useRef(value);
  useEffect(() => {
    const controls = animate(prevValue.current, value, {
      duration: 0.8,
      type: 'spring',
      bounce: 0.15,
      onUpdate: v => {
        if (nodeRef.current) {
          nodeRef.current.textContent = prefix + Math.round(v).toLocaleString('en-US');
        }
      }
    });
    prevValue.current = value;
    return controls.stop;
  }, [value, prefix]);
  return <span ref={nodeRef} className="font-mono tracking-tight glow-text-emerald" data-id="element-14">
      {prefix}
      {value.toLocaleString('en-US')}
    </span>;
};
const Gauge = ({
  percent
}: {
  percent: number;
}) => {
  const radius = 60;
  const circumference = Math.PI * radius;
  const strokeDashoffset = circumference - percent / 100 * circumference;
  return <div className="relative flex items-center justify-center w-48 h-24 overflow-hidden" data-id="element-15">
      <svg className="w-full h-full" viewBox="0 0 140 70" data-id="element-16">
        <path d="M 10 60 A 60 60 0 0 1 130 60" fill="none" stroke="#1e293b" strokeWidth="12" strokeLinecap="square" data-id="element-17" />
        <motion.path d="M 10 60 A 60 60 0 0 1 130 60" fill="none" stroke="#10b981" strokeWidth="12" strokeLinecap="square" strokeDasharray={circumference} initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset
      }} transition={{
        type: 'spring',
        bounce: 0,
        duration: 1
      }} style={{
        filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))'
      }} data-id="element-18" />
      </svg>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex flex-col items-center" data-id="element-19">
        <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-0.5 font-mono" data-id="element-20">
          SYS_LOAD
        </span>
        <AnimatedNumber value={percent} prefix="" data-id="element-21" />
        <span className="font-mono text-emerald-400 text-sm" data-id="element-22">%</span>
      </div>
    </div>;
};
export function Calculator() {
  const [devCount, setDevCount] = useState<number>(50);
  const [selectedTool, setSelectedTool] = useState(TOOLS[0].id);
  const [utilization, setUtilization] = useState<number>(80);
  const [monthlySpend, setMonthlySpend] = useState(0);
  const [annualSpend, setAnnualSpend] = useState(0);
  const [wastedSpend, setWastedSpend] = useState(0);
  useEffect(() => {
    const tool = TOOLS.find(t => t.id === selectedTool) || TOOLS[0];
    const activeDevs = Math.round(devCount * (utilization / 100));
    const inactiveDevs = devCount - activeDevs;
    let monthly = 0;
    let waste = 0;
    if (tool.type === 'per_seat') {
      monthly = devCount * tool.basePrice;
      waste = inactiveDevs * tool.basePrice;
    } else {
      monthly = activeDevs * tool.basePrice;
      waste = monthly * 0.15;
    }
    setMonthlySpend(monthly);
    setAnnualSpend(monthly * 12);
    setWastedSpend(waste * 12);
  }, [devCount, selectedTool, utilization]);
  return <div className="bg-slate-950 h-full flex flex-col relative" data-id="element-23">
      {/* Decorative scanline overlay */}
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.2)_50%)] bg-[length:100%_4px] opacity-30 z-0" data-id="element-24"></div>

      <div className="bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between relative z-10" data-id="element-25">
        <div className="flex items-center gap-3" data-id="element-26">
          <CalcIcon className="text-emerald-500" size={18} data-id="element-27" />
          <h2 className="text-xs font-mono uppercase tracking-widest text-slate-300" data-id="element-28">
            MODULE :: TOKEN_METER_FREE
          </h2>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-500 bg-emerald-500/10 px-2 py-1 border border-emerald-500/20" data-id="element-29">
          <span className="w-1.5 h-1.5 bg-emerald-500 animate-pulse" data-id="element-30"></span>
          LIVE_TELEMETRY
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 xl:grid-cols-12 relative z-10" data-id="element-31">
        {/* Inputs */}
        <div className="xl:col-span-5 p-6 md:p-8 space-y-8 border-b xl:border-b-0 xl:border-r border-slate-800 bg-slate-950/50" data-id="element-32">
          {/* Dev Count */}
          <div data-id="element-33">
            <label className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-slate-400 mb-4" data-id="element-34">
              <span className="flex items-center gap-2" data-id="element-35">
                <Users size={14} className="text-slate-600" data-id="element-36" /> ENG_TEAM_SIZE
              </span>
              <span className="text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5" data-id="element-37">
                [{devCount}]
              </span>
            </label>
            <input type="range" min="10" max="1000" step="10" value={devCount} onChange={e => setDevCount(parseInt(e.target.value))} className="w-full h-1 bg-slate-800 appearance-none cursor-pointer accent-emerald-500" data-id="element-38" />
            <div className="flex justify-between text-[10px] text-slate-600 mt-2 font-mono" data-id="element-39">
              <span data-id="element-40">10</span>
              <span data-id="element-41">1,000+</span>
            </div>
          </div>

          {/* Tool Selection */}
          <div data-id="element-42">
            <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-400 mb-4" data-id="element-43">
              <Code2 size={14} className="text-slate-600" data-id="element-44" /> PRIMARY_AI_TOOL
            </label>
            <div className="grid grid-cols-2 gap-2" data-id="element-45">
              {TOOLS.map(tool => <button key={tool.id} onClick={() => setSelectedTool(tool.id)} className={`p-2 text-left border transition-all relative overflow-hidden ${selectedTool === tool.id ? 'border-emerald-500 bg-emerald-500/10 glow-box-emerald' : 'border-slate-800 bg-slate-900 hover:border-slate-600'}`} data-id="element-46">
                  <div className={`font-mono text-[11px] uppercase tracking-wider truncate ${selectedTool === tool.id ? 'text-emerald-400' : 'text-slate-400'}`} data-id="element-47">
                    {tool.name}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1 font-mono" data-id="element-48">
                    ${tool.basePrice}/{tool.type === 'per_seat' ? 'mo' : 'est'}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Utilization */}
          <div data-id="element-49">
            <label className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-slate-400 mb-4" data-id="element-50">
              <span className="flex items-center gap-2" data-id="element-51">
                <TrendingUp size={14} className="text-slate-600" data-id="element-52" />{' '}
                EST_ACTIVE_USAGE
              </span>
              <span className="text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5" data-id="element-53">
                [{utilization}%]
              </span>
            </label>
            <input type="range" min="10" max="100" step="5" value={utilization} onChange={e => setUtilization(parseInt(e.target.value))} className="w-full h-1 bg-slate-800 appearance-none cursor-pointer accent-emerald-500" data-id="element-54" />
            <p className="text-[10px] font-mono text-slate-600 mt-2 uppercase" data-id="element-55">
              % of provisioned seats generating code weekly.
            </p>
          </div>
        </div>

        {/* Outputs */}
        <div className="xl:col-span-7 p-6 md:p-8 flex flex-col justify-center relative overflow-hidden bg-slate-900/20" data-id="element-56">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" data-id="element-57"></div>

          <div className="space-y-6 relative z-10" data-id="element-58">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-slate-950 p-6 border border-slate-800" data-id="element-59">
              <div className="flex-1 w-full" data-id="element-60">
                <div className="text-[10px] font-mono text-slate-500 mb-1 uppercase tracking-widest" data-id="element-61">
                  &gt; MONTHLY_RUN_RATE
                </div>
                <div className="text-4xl font-bold text-emerald-400" data-id="element-62">
                  <AnimatedNumber value={monthlySpend} prefix="$" data-id="element-63" />
                </div>
                <div className="mt-4 pt-4 border-t border-slate-800 border-dashed" data-id="element-64">
                  <div className="text-[10px] font-mono text-slate-500 mb-1 uppercase tracking-widest" data-id="element-65">
                    &gt; ANNUAL_PROJECTED
                  </div>
                  <div className="text-xl font-bold text-slate-300" data-id="element-66">
                    <AnimatedNumber value={annualSpend} prefix="$" data-id="element-67" />
                  </div>
                </div>
              </div>
              <div className="shrink-0 flex justify-center w-full sm:w-auto" data-id="element-68">
                <Gauge percent={utilization} data-id="element-69" />
              </div>
            </div>

            <motion.div layout className="bg-red-500/5 border border-red-500/20 p-5 flex items-start gap-4" data-id="element-70">
              <div className="text-red-500 shrink-0 mt-1" data-id="element-71">
                <AlertCircle size={18} data-id="element-72" />
              </div>
              <div data-id="element-73">
                <h4 className="text-[10px] font-mono font-bold text-red-500 uppercase tracking-widest" data-id="element-74">
                  WARN: EST_ANNUAL_WASTE
                </h4>
                <div className="text-2xl font-bold text-red-400 my-1 font-mono tracking-tight" style={{
                textShadow: '0 0 15px rgba(248, 113, 113, 0.3)'
              }} data-id="element-75">
                  <AnimatedNumber value={wastedSpend} prefix="$" data-id="element-76" />
                </div>
                <p className="text-xs font-mono text-red-400/60 mt-2 uppercase" data-id="element-77">
                  {TOOLS.find(t => t.id === selectedTool)?.type === 'per_seat' ? 'Paying for idle provisioned seats.' : 'Est. token inefficiency & context waste.'}
                </p>
              </div>
            </motion.div>

            <div className="pt-4" data-id="element-78">
              <a href="https://waydev.co/pricing" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-slate-950 px-6 py-4 font-mono font-bold hover:bg-emerald-400 transition-colors glow-box-emerald text-sm uppercase tracking-widest" data-id="element-79">
                [ INIT_OPTIMIZATION ]
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>;
}